// ─────────────────────────────────────────────────────────────────────────────
// MobiConnect WebAPI v1 — tip tanimlari (OAS 3.0 semasindan).
// Sadece bu modulun kullandigi alanlar; sema buyudukce buraya ekle.
// ─────────────────────────────────────────────────────────────────────────────

// ─── Oturum ──────────────────────────────────────────────────────────────────

export interface LogoLoginRequest {
  username: string;
  password: string;
  firmaNo: number;
}

export interface LogoSessionStatus {
  connected: boolean;
  firm: number;
  period: number;
}

// ─── Cari (Customer) ─────────────────────────────────────────────────────────
// POST /api/customers govdesi. Insert icin id = 0.
// Logo cari kart tipi: cardType (0=alici+satici, 3=alici, 4=satici ... Logo'ya gore).

export interface Customer {
  id: number;
  code: string;
  title: string;
  title2?: string;
  cardType?: number;
  profileId?: number;

  specode1?: string;
  specode2?: string;
  specode3?: string;
  specode4?: string;
  specode5?: string;

  address1?: string;
  address2?: string;
  addressNo?: string;
  districtCode?: string;
  district?: string;
  townCode?: string;
  town?: string;
  cityCode?: string;
  city?: string;
  countryCode?: string;
  country?: string;
  zipCode?: string;

  phone1?: string;
  phone1Code?: string;
  phone1Ext?: string;
  phone2?: string;
  phone2Code?: string;
  phone2Ext?: string;
  fax?: string;
  faxCode?: string;
  faxExt?: string;
  email?: string;
  email2?: string;
  email3?: string;
  webUrl?: string;

  contact?: string;
  contact2?: string;
  contact3?: string;
  contact1Tel?: string;
  contact1TelCode?: string;
  contact1TelExt?: string;
  contact2Tel?: string;
  contact2TelCode?: string;
  contact2TelExt?: string;
  contact3Tel?: string;
  contact3TelCode?: string;
  contact3TelExt?: string;

  name?: string;
  surname?: string;

  vatId?: string;        // Vergi No
  tcKimlikNo?: string;   // TC Kimlik No
  taxOffice?: string;    // Vergi Dairesi
  persCompany?: number;  // 0=tuzel, 1=sahis (Logo'ya gore)

  creditLimit?: number;
  riskType1?: number;
  riskType2?: number;
  riskType3?: number;

  paymentGroup?: string;
  tradingGroup?: string;
  groupCode?: string;

  // Islem yetkileri (browse flag'leri) ve fis basim sayilari
  purchBrws?: number;
  salesBrws?: number;
  impBrws?: number;
  expBrws?: number;
  finBrws?: number;
  invoicePrintCount?: number;
  dispPrintCount?: number;
  ordPrintCount?: number;
  createWhFiche?: number;

  orgLogoId?: string;
  factoryDivNr?: number;
  lowLevelCodes1?: number;
  isActive: boolean;
}

/** Yeni cari icin bos taslak. */
export function emptyCustomer(): Customer {
  return {
    id: 0,
    code: '',
    title: '',
    cardType: 3,
    isActive: true,
  };
}

// ─── Liste filtreleri ────────────────────────────────────────────────────────
// GET /api/customers query parametreleri.

export interface CustomerFilter {
  onlyActive?: boolean;
  code?: string;
  title?: string;
  contact?: string;
  city?: string;
  town?: string;
  countryCode?: string;
  country?: string;
  zipCode?: string;
  phone?: string;
  email?: string;
  specode1?: string;
  specode2?: string;
  groupCode?: string;
  cardType?: number;
}

// ─── API sonuc sarmalayicisi ─────────────────────────────────────────────────
// Cagri sonucunu UI'da tek tipte ele almak icin.

export interface LogoResult<T = unknown> {
  ok: boolean;
  data?: T;
  status?: number;
  message?: string;
}

// ─── Malzeme (Material) ──────────────────────────────────────────────────────

export interface MaterialUnit {
  unitCode?: string;
  convFact1?: number;
  convFact2?: number;
  width?: number;
  length?: number;
  height?: number;
  area?: number;
  volume?: number;
  grossVolume?: number;
  weight?: number;
  grossWeight?: number;
  weightCode?: string;
  grossWeightCode?: string;
  barcodes?: string[];
}

export interface Material {
  id: number;
  code: string;
  name: string;
  name2?: string;
  name3?: string;
  name4?: string;
  cardType?: number;
  unitSetCode?: string;     // birim seti
  groupCode?: string;
  producerCode?: string;    // uretici kodu

  // Ozel kodlar
  auxilCode?: string;       // Ozel Kod1
  auxilCode2?: string;
  auxilCode3?: string;
  auxilCode4?: string;
  auxilCode5?: string;

  // Kullanim bayraklari
  usefPurchasing?: boolean;
  usefSales?: boolean;
  usefMm?: boolean;
  vat?: number;             // KDV

  // Override bayraklari (null=default)
  tool?: number | null;
  autoIncSl?: number | null;
  lotsDivisible?: number | null;
  imageInc?: number | null;
  img2Inc?: number | null;
  updateChilds?: number | null;
  extAccFlags?: number | null;
  extCrdFlags?: number | null;
  multiAddTax?: number | null;
  packet?: number | null;
  addTaxPurchBrws?: number | null;
  addTaxSalesBrws?: number | null;
  distLotUnits?: number | null;
  combLotUnits?: number | null;

  // KDV detayi
  selVat?: number;
  returnVat?: number;
  selPrVat?: number;
  returnPrVat?: number;

  // Tevkifat
  saleDeductionPart1?: number;
  saleDeductionPart2?: number;
  purchDeductionPart1?: number;
  purchDeductionPart2?: number;
  deductCode?: string;
  deductDef?: string;
  purchDeductCode?: string;
  canDeduct?: number;
  appSpeVatMatrah?: number;

  // Lojistik / is kurallari
  paymentCode?: string;
  shelfLife?: number;
  markCode?: string;
  distPoint?: number;
  avrWhDuration?: number;
  pordAmountTolerance?: number;
  sordAmountTolerance?: number;
  noDiscount?: number;
  profitMarginRate?: number;
  draftOfferBrws?: number;
  tibbiCihaz?: number;

  units?: MaterialUnit[];
  isActive: boolean;
}

export function emptyMaterial(): Material {
  return { id: 0, code: '', name: '', cardType: 1, usefSales: true, usefPurchasing: true, isActive: true, units: [] };
}

// ─── Tedarikci (Supplier) ────────────────────────────────────────────────────

export interface Supplier {
  id: number;
  code: string;
  title: string;
  title2?: string;
  specode1?: string;
  specode2?: string;
  specode3?: string;
  specode4?: string;
  specode5?: string;
  address1?: string;
  address2?: string;
  city?: string;
  countryCode?: string;
  country?: string;
  zipCode?: string;
  phone1?: string;
  phone2?: string;
  fax?: string;
  email?: string;
  paymentGroup?: string;
  tradingGroup?: string;
  cardType?: number;    // varsayilan 2
  isActive: boolean;
}

export function emptySupplier(): Supplier {
  return { id: 0, code: '', title: '', cardType: 2, isActive: true };
}

// ─── Satis Fiyati (ItemSalesPrice) ───────────────────────────────────────────

export interface ItemSalesPrice {
  cardCode: string;      // malzeme kodu
  price: number;
  currency?: number;
  vatIncl?: boolean;
  dateBegin?: string;
  dateEnd?: string;
  clientCode?: string;   // cari
  arpTradingGroup?: string;
  arpAuthCode?: string;
  arpSpecode1?: string;
  arpSpecode2?: string;
  arpSpecode3?: string;
  arpSpecode4?: string;
  arpSpecode5?: string;
  priority?: number;
  unitCode?: string;
  paymentCode?: string;
  shipmentType?: number;
  orgLogoId?: string;
  definition?: string;   // aciklama
  tradingGroup?: string;
  groupCode?: string;
  orderNr?: number;
  cyphCode?: string;
  arpCyphCode?: string;
  extAccFlags?: number;
}

export function emptySalesPrice(): ItemSalesPrice {
  return { cardCode: '', price: 0, vatIncl: false };
}

// ─── Alis Fiyati (ItemPurchasePrice) ─────────────────────────────────────────

export interface ItemPurchasePrice {
  itemCode: string;
  clientCode?: string;   // tedarikci
  price: number;
  unitCode?: string;
  vatIncl?: boolean;
  currency?: number;
  dateBegin?: string;
  dateEnd?: string;
  priority?: number;
  arpSpecode1?: string;
  arpSpecode2?: string;
  arpSpecode3?: string;
  arpSpecode4?: string;
  arpSpecode5?: string;
  arpTradingGroup?: string;
  arpAuthCode?: string;
  divCodes?: string;     // isyeri kodlari
}

export function emptyPurchasePrice(): ItemPurchasePrice {
  return { itemCode: '', price: 0, vatIncl: false };
}

// ─── Ambar Parametreleri (ItemWarehouseParam) ────────────────────────────────

export interface WarehouseParamLine {
  whNumber?: number;
  whCode?: string;        // ambar adi
  minLevel?: number;
  maxLevel?: number;
  safetyLevel?: number;
  ioFlag?: number;        // giris kontrolu
  outFlag?: number;       // cikis kontrolu
  minLevelFlag?: number;
  maxLevelFlag?: number;
  safetyLevelFlag?: number;
  backorderFlag?: number; // negatif stok
  locationCode?: string;  // stok yeri
  abcCode?: string;
}

export interface ItemWarehouseParam {
  itemCode: string;
  lines: WarehouseParamLine[];
}

export function emptyWarehouseParam(): ItemWarehouseParam {
  return { itemCode: '', lines: [] };
}

// ─── Siparis (Order) ─────────────────────────────────────────────────────────

export interface OrderLine {
  lineNum?: number;
  materialCode?: string;
  materialName?: string;
  quantity?: number;
  price?: number;
  total?: number;
  vatRate?: number;
  vatAmount?: number;
  totalNet?: number;
  unitCode?: string;
  unitConv1?: number;
  unitConv2?: number;
  producerCode?: string;
}

export interface Order {
  id: number;
  orderNumber?: string;
  orderDate?: string;
  clientCode?: string;
  clientName?: string;
  description?: string;
  salesmanCode?: string;
  paymentCode?: string;
  orderStatus?: number;   // 4=Onayli
  totalGross?: number;
  totalVat?: number;
  totalNet?: number;
  lines: OrderLine[];
}

export function emptyOrder(): Order {
  return { id: 0, lines: [] };
}
